import {store} from '../store';
import {renderCustomerFields} from './customer';
import {Environment, startModalLoading, stopModalLoading} from '../reducers/environmentReducer';
import {PeachPayCustomer} from '../reducers/peachPayCustomerReducer';
import {$qs} from '../../../@shared/ts/dom';

/* Makes sure the modal responds to window resize and scrolling WHILE the mobile slide-up view is open. */
function handleResponsiveness() {
	const ppModalContent = $qs('#pp-modal-content');
	if (ppModalContent) {
		let location;
		let mobileWidth;
		if (ppModalContent.classList.contains('pp-content-mobile')) {
			location = 'mobile';
			mobileWidth = '666px';
		} else {
			location = 'new';
			mobileWidth = '900px';
		}

		if (window.matchMedia(`(max-width: ${mobileWidth})`).matches) {
			$qs(`#pp-${location}-customer-checkout`)?.classList.add('pp-dark-blur');
			$qs('.pp-close')?.classList.add('pp-dark-blur');
			ppModalContent.style.height = '100vh';
			ppModalContent.style.overflow = 'hidden';
		} else {
			$qs(`#pp-${location}-customer-checkout`)?.classList.remove('pp-dark-blur');
			$qs('.pp-close')?.classList.remove('pp-dark-blur');
			ppModalContent?.style.removeProperty('height');
			ppModalContent?.style.removeProperty('overflow');
		}
	}
}

/* Opens the slide-up view of the id #pp-slide-up-<modaName>-mobile. */
export function openSlideUpView(modalName: string) {
	const backgroundElement = $qs(`#pp-slide-up-${modalName}-mobile .pp-slide-up-view-bg`);
	const backarrow = $qs(`#pp-slide-up-${modalName}-mobile .pp-slide-up-header`);

	$qs(`#pp-slide-up-${modalName}-mobile`)?.classList.add('expanded');
	$qs('.pp-close')?.scrollIntoView();
	window.addEventListener('resize', handleResponsiveness);
	setTimeout(() => {
		handleResponsiveness();
	}, 100);
	if (modalName === 'cart') {
		$qs('#pp-dropdown-new')?.setAttribute('aria-expanded', 'true');
	}

	/* Closes the slide-up view. */
	const tapToClose = (e: Event) => {
		e.stopImmediatePropagation();
		closeSlideUpView(modalName);
		backgroundElement?.removeEventListener('click', tapToClose);
		backarrow?.removeEventListener('click', tapToClose);
		window.removeEventListener('resize', handleResponsiveness);
		if (modalName === 'cart') {
			const dropDown = $qs('#pp-dropdown-new');
			dropDown?.removeEventListener('click', tapToClose);
			dropDown?.removeEventListener('keypress', closeCartWithKeyPress);
			dropDown?.addEventListener('keypress', openCartWithKeyPress);
			dropDown?.addEventListener('click', openCart);
		} else {
			$qs(`#pp-${modalName}-cancel`)?.removeEventListener('click', tapToClose);
		}
	};

	/* Closes the slide-up view only after asking whether the unsaved changes don't need to be saved
	and the customer confirms. */
	const cancelAndClose = (e: Event) => {
		e.stopImmediatePropagation();
		const modalPage = Environment.modalUI.page();
		const formID = modalPage === 'info' ? '#pp-info-form' : '#pp-different-shipping-details';
		const infoFormValidity = $qs<HTMLFormElement>(`${formID}`)?.checkValidity() ?? false;
		if ($qs(`#pp-${modalName}-mobile-save`)?.hasAttribute('disabled')) {
			if (!infoFormValidity) {
				openSlideUpView('ship-to');
				$qs<HTMLFormElement>(`${formID}`)?.reportValidity();
				return;
			}

			closeSlideUpView(modalName);
			backgroundElement?.removeEventListener('click', cancelAndClose);
			backarrow?.removeEventListener('click', cancelAndClose);
			window.removeEventListener('resize', handleResponsiveness);
			$qs(`#pp-${modalName}-cancel .exit-back-btn`)?.removeEventListener('click', cancelAndClose);
		} else {
			if (!infoFormValidity) {
				openSlideUpView('ship-to');
				$qs<HTMLFormElement>(`${formID}`)?.reportValidity();
				return;
			}

			// eslint-disable-next-line no-alert
			if (confirm('Close without saving changes?')) {
				store.dispatch(startModalLoading());
				closeSlideUpView(modalName);
				backgroundElement?.removeEventListener('click', cancelAndClose);
				backarrow?.removeEventListener('click', cancelAndClose);
				window.removeEventListener('resize', handleResponsiveness);
				$qs(`#pp-${modalName}-cancel .exit-back-btn`)?.removeEventListener('click', cancelAndClose);
				renderCustomerFields(PeachPayCustomer.data());
				$qs(`#pp-${modalName}-mobile-save`)?.setAttribute('disabled', '');
				store.dispatch(stopModalLoading());
			}
		}
	};

	/* Wraps around the tapToClose function to check the key. */
	const closeCartWithKeyPress = (event: KeyboardEvent) => {
		event.preventDefault();
		event.stopImmediatePropagation();
		if (event.key === 'Enter' || event.key === ' ') {
			tapToClose(event);
		}
	};

	/* Adding more event-listeners */
	if (modalName === 'ship-to') {
		backgroundElement?.addEventListener('click', cancelAndClose);
		backarrow?.addEventListener('click', cancelAndClose);
		$qs(`#pp-${modalName}-cancel .exit-back-btn`)?.addEventListener('click', cancelAndClose);
	} else {
		backgroundElement?.addEventListener('click', tapToClose);
		backarrow?.addEventListener('click', tapToClose);
		if (modalName !== 'cart') {
			$qs(`#pp-${modalName}-cancel`)?.addEventListener('click', tapToClose);
		}
	}

	if (modalName === 'cart') {
		const dropDown = $qs('#pp-dropdown-new');
		dropDown?.addEventListener('click', tapToClose);
		dropDown?.addEventListener('keypress', closeCartWithKeyPress);
		dropDown?.removeEventListener('click', openCart);
		dropDown?.removeEventListener('keypress', openCartWithKeyPress);
	}
}

/* Closes the slide-up view and cleans up the modal UI. */
export function closeSlideUpView(modalName: string) {
	const ppModalContent = $qs('#pp-modal-content');
	if (ppModalContent) {
		ppModalContent.style.removeProperty('height');
		ppModalContent.style.removeProperty('overflow');
	}

	const location = ppModalContent?.classList.contains('pp-content-mobile') ? 'mobile' : 'new';
	$qs(`#pp-slide-up-${modalName}-mobile`)?.classList.remove('expanded');
	$qs(`#pp-${location}-customer-checkout`)?.classList.remove('pp-dark-blur');
	$qs('.pp-close')?.classList.remove('pp-dark-blur');
	if (modalName === 'cart') {
		$qs('#pp-dropdown-new')?.setAttribute('aria-expanded', 'false');
	}
}

function openCart() {
	openSlideUpView('cart');
}

/* Checks the key value before opening the cart. */
function openCartWithKeyPress(event: KeyboardEvent) {
	event.preventDefault();
	event.stopImmediatePropagation();
	if (event.key === 'Enter' || event.key === ' ') {
		openSlideUpView('cart');
	}
}

export {
	openCart,
	openCartWithKeyPress,
};
